/**
 * BioPushOptInSheet — anonymous visitor prompt on `/u/{slug}` pages.
 *
 * v147.33 (Feb 2026) — Ask visitors to enable browser push notifications
 * for THIS brand only. Uses the same FCM SDK path as the signed-in
 * device registration, but the resulting token is stored per-brand on
 * the server (`bio_push_subscriptions`) so a single browser can
 * subscribe to multiple brands independently.
 *
 * Trigger rules
 * -------------
 *   1. Auto-open ~5s after the bio page mounts (dismissible; a "not now"
 *      snoozes the sheet for 7 days on this device via localStorage).
 *   2. `openNow()` (exposed via a ref) — called by the follow flow after
 *      a successful `/public/creators/:slug/follow` so the visitor sees
 *      the ask while momentum is highest.
 *
 * Failure paths (all surface a helpful toast + close the sheet):
 *   * Browser rejects permission → soft dismiss for 30 days.
 *   * Feature disabled on this brand → sheet never opens.
 *   * Cap reached → sheet renders but Enable button greys out.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, Sparkles, CheckCircle2 } from "lucide-react";

const SNOOZE_KEY = (slug) => `linkola:bio_push_snoozed:${slug}`;
const SNOOZE_MS_NOT_NOW = 7 * 24 * 60 * 60 * 1000;
const SNOOZE_MS_DENIED = 30 * 24 * 60 * 60 * 1000;
const AUTO_OPEN_DELAY_MS = 5000;

/**
 * Register the browser's FCM token against this brand. Extracted so the
 * follow-flow and the sheet's Enable button share one code path.
 * Returns `{ok, reason?}`.
 */
// v147.46 — Visitor identity is stored in BOTH localStorage AND a
// long-lived cookie so it survives most "clear browsing data"
// variants (clearing cache alone doesn't touch cookies; clearing
// site data alone doesn't touch cookies from a different subdomain).
// Cookie is `SameSite=Lax; Secure; Max-Age=1 year` so it also rides
// on the automatic status ping — the server uses it to look up
// whether this browser was previously subscribed to THIS brand and
// authorises a silent re-subscribe.
function readVisitorId() {
  let vid = null;
  try { vid = localStorage.getItem("linkola_visitor_id"); } catch (_) { /* noop */ }
  if (!vid) {
    try {
      const m = document.cookie.match(/(?:^|;\s*)linkola_visitor_id=([^;]+)/);
      if (m) vid = decodeURIComponent(m[1]);
    } catch (_) { /* noop */ }
  }
  return vid || null;
}

function persistVisitorId(vid) {
  if (!vid) return;
  try { localStorage.setItem("linkola_visitor_id", vid); } catch (_) { /* noop */ }
  try {
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `linkola_visitor_id=${encodeURIComponent(vid)}; path=/; max-age=${oneYear}; SameSite=Lax; Secure`;
  } catch (_) { /* noop */ }
}


async function subscribeFlow(slug) {
  if (typeof Notification === "undefined") {
    return { ok: false, reason: "unsupported" };
  }
  // Browser permission — this ALSO opens the OS prompt when default.
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return { ok: false, reason: "permission_denied" };
  }
  const backend = process.env.REACT_APP_BACKEND_URL;
  // Fetch the Firebase web config + VAPID key.
  let cfg;
  try {
    cfg = await (await fetch(`${backend}/api/auth/firebase/public-config`)).json();
  } catch (_) {
    return { ok: false, reason: "config_failed" };
  }
  if (!cfg?.configured) return { ok: false, reason: "not_configured" };
  // Init a firebase app (safe if already inited elsewhere).
  const { initializeApp, getApps, getApp } = await import("firebase/app");
  let app;
  try { app = getApp(); } catch { app = null; }
  if (!app) {
    app = getApps().length ? getApps()[0] : initializeApp({
      apiKey: cfg.apiKey, authDomain: cfg.authDomain, projectId: cfg.projectId,
      appId: cfg.appId, messagingSenderId: cfg.messagingSenderId,
      storageBucket: cfg.storageBucket,
    });
  }
  // Register the SW (same one used by signed-in pushes) so background
  // pushes land on the visitor even after they close the tab.
  let swRegistration;
  if ("serviceWorker" in navigator) {
    try {
      swRegistration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        { scope: "/firebase-cloud-messaging-push-scope" },
      );
    } catch (e) {
      console.warn("[bio-push] SW register failed", e);
    }
  }
  const { getMessaging, getToken, isSupported } = await import("firebase/messaging");
  if (!(await isSupported())) return { ok: false, reason: "unsupported" };
  const messaging = getMessaging(app);
  const vapidKey = (cfg.vapidKey && cfg.vapidKey.trim()) || undefined;
  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });
  if (!token) return { ok: false, reason: "no_token" };
  // POST to backend under this brand's slug.
  let visitor_id = readVisitorId();
  const res = await fetch(
    `${backend}/api/public/creators/${encodeURIComponent(slug)}/bio-push/subscribe`,
    { method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fcm_token: token, visitor_id }) },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    return { ok: false, reason: body || `http_${res.status}` };
  }
  // v147.46 — Persist visitor_id in the durable cookie too so the
  // status ping can auto-restore the subscription after a partial
  // browser-data clear.
  try {
    const j = await res.json().catch(() => ({}));
    if (j.visitor_id) persistVisitorId(j.visitor_id);
    else if (visitor_id) persistVisitorId(visitor_id);
  } catch (_) { /* noop */ }
  return { ok: true };
}


// v147.45 — localStorage key that remembers "this visitor already
// subscribed to this brand on this device". The `bio-push/status`
// endpoint can only verify a fcm_token by round-tripping it, but that
// costs an unnecessary FCM mint on every page load. Instead we cache
// the subscription state client-side so the sheet stays hidden after
// a successful "Enable" tap. Cleared automatically when the user
// clears browser data — at which point their FCM token is regenerated
// too, so they're a fresh visitor and the sheet reappears (expected).
const SUBSCRIBED_KEY = (slug) => `bio_push_subscribed_${slug}`;
function prev_isSubscribedLocally(slug) {
  try { return localStorage.getItem(SUBSCRIBED_KEY(slug)) === "1"; }
  catch (_) { return false; }
}


export default function BioPushOptInSheet({ slug, brandName, brandPicture, autoTriggerRef }) {
  // v147.45 — Prime `subscribed` from localStorage on first render so
  // the sheet never flashes for visitors who've already opted in on
  // this device. The subsequent status fetch just refreshes brand
  // name / picture — the `subscribed` field from the server is
  // ignored because the endpoint can't identify a browser without
  // its fcm_token (which we don't want to mint just for a status
  // check).
  const initialSubscribed = (() => {
    try { return localStorage.getItem(SUBSCRIBED_KEY(slug)) === "1"; }
    catch (_) { return false; }
  })();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState({ enabled: false, subscribed: initialSubscribed, cap_reached: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const openedRef = useRef(false);

  // Pull status once on mount + expose an imperative `openNow()`.
  // v147.46 — Pass `visitor_id` from cookie/localStorage so the server
  // can tell us whether this browser was PREVIOUSLY subscribed to
  // this brand (survives partial browser-data clears, e.g. cache-only).
  // If yes AND browser permission is still granted, we silently
  // re-mint the FCM token and re-subscribe — no prompt needed because
  // the visitor explicitly consented before.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const vid = readVisitorId();
        const url = new URL(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/creators/${encodeURIComponent(slug)}/bio-push/status`,
        );
        if (vid) url.searchParams.set("visitor_id", vid);
        const r = await fetch(url.toString());
        const j = await r.json();
        if (cancelled) return;
        setStatus((prev) => ({
          enabled: !!j.enabled,
          subscribed: prev.subscribed,
          cap_reached: !!j.cap_reached,
          brand_name: j.brand_name,
          brand_picture: j.brand_picture,
        }));
        // Silent auto-resubscribe path — only fires when we already
        // know this exact visitor consented to THIS brand before.
        if (
          j.enabled
          && j.previously_subscribed
          && !prev_isSubscribedLocally(slug)
          && typeof Notification !== "undefined"
          && Notification.permission === "granted"
        ) {
          try {
            const rr = await subscribeFlow(slug);
            if (rr.ok && !cancelled) {
              try { localStorage.setItem(SUBSCRIBED_KEY(slug), "1"); } catch (_) {}
              setStatus((s) => ({ ...s, subscribed: true }));
            }
          } catch (_) { /* silent — falls through to explicit sheet */ }
        }
      } catch (_) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Auto-open after 5s (respects snooze + browser support + already-subscribed).
  // v147.43 + v147.44 — Rules:
  //   * `subscribed=true` (already opted in for THIS brand) — never show.
  //   * `Notification.permission === "denied"` — hide (Chrome/Safari
  //     won't re-prompt).
  //   * Snooze key present (only set on browser "Block" / unsupported
  //     browsers) — hide.
  //   * Otherwise — show the sheet after 5s, EVERY page visit, until
  //     the visitor either taps "Enable" (subscribed=true then) or
  //     the browser marks the permission as `denied`. The old flow
  //     snoozed for 7 days on "Not now", which meant most visitors
  //     never saw the offer again → poor opt-in rate. Now the sheet
  //     comes back on every visit until they commit either way, and
  //     "Not now" is a soft dismiss for THIS page load only.
  useEffect(() => {
    if (!status.enabled) return;
    if (status.subscribed) return;
    if (typeof Notification === "undefined") return;

    if (Notification.permission === "denied") return;

    try {
      const s = localStorage.getItem(SNOOZE_KEY(slug));
      if (s && Number(s) > Date.now()) return;
    } catch (_) { /* noop */ }
    const h = window.setTimeout(() => {
      if (openedRef.current) return;
      openedRef.current = true;
      setOpen(true);
    }, AUTO_OPEN_DELAY_MS);
    return () => window.clearTimeout(h);
  }, [status.enabled, status.subscribed, slug]);

  // Imperative trigger for follow / coupon-claim flows.
  useEffect(() => {
    if (!autoTriggerRef) return;
    autoTriggerRef.current = () => {
      if (!status.enabled) return;
      if (status.subscribed) return;
      // v147.43 — Show the sheet even when permission is already
      // granted so the visitor confirms they want THIS brand's pushes
      // (browser permission is origin-wide, subscriptions are per-brand).
      openedRef.current = true;
      setOpen(true);
    };
  }, [autoTriggerRef, status.enabled, status.subscribed]);

  const snooze = (ms) => {
    try { localStorage.setItem(SNOOZE_KEY(slug), String(Date.now() + ms)); } catch (_) { /* noop */ }
  };

  // v147.44 — "Not now" no longer snoozes the sheet. The user asked
  // for the prompt to reappear every time they open the bio page
  // until they've explicitly turned notifications on. Only permission
  // denial (browser-level "Block") and unsupported browsers snooze —
  // in both cases the sheet can't do anything useful, so hiding it
  // avoids nagging. Keeping the helper here so those two paths still
  // silence the sheet cleanly.
  const notNow = () => {
    setOpen(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await subscribeFlow(slug);
      if (r.ok) {
        // v147.45 — Remember on THIS device that the visitor opted in
        // to THIS brand so the sheet never re-appears on future visits.
        try { localStorage.setItem(SUBSCRIBED_KEY(slug), "1"); } catch (_) { /* noop */ }
        setDone(true);
        setStatus((s) => ({ ...s, subscribed: true }));
      } else if (r.reason === "permission_denied") {
        snooze(SNOOZE_MS_DENIED);
        toast.info("You can enable notifications later from your browser settings.");
        setOpen(false);
      } else if (r.reason === "unsupported") {
        toast.error("Your browser doesn't support push notifications.");
        snooze(SNOOZE_MS_DENIED);
        setOpen(false);
      } else if (String(r.reason).includes("cap_reached")) {
        toast.info("This brand's notification list is temporarily full — try again later.");
        setOpen(false);
      } else {
        toast.error("Could not enable notifications — please try again.");
        setOpen(false);
      }
    } catch (e) {
      toast.error("Could not enable notifications — please try again.");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!status.enabled || status.subscribed || !open) return null;

  const label = brandName || status.brand_name || "this brand";
  const picture = brandPicture || status.brand_picture;

  return (
    <>
      {/* v147.44 — Backdrop is a plain div (not a button) and has NO
          click handler, so tapping anywhere outside the sheet does
          NOT dismiss it. The visitor must tap "Not now" or "Enable"
          explicitly. Prevents accidental dismissals from thumb
          taps while scrolling. */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: "rgba(22,22,22,0.45)" }}
        data-testid="bio-push-optin-backdrop"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-labelledby="bio-push-optin-title"
        aria-modal="true"
        className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md animate-in slide-in-from-bottom"
        style={{
          background: "#FFFFFF",
          borderTop: "1px solid #EAE6DE",
          borderTopLeftRadius: "20px",
          borderTopRightRadius: "20px",
          boxShadow: "0 -12px 32px rgba(22,22,22,0.16)",
          paddingBottom: "env(safe-area-inset-bottom,0)",
        }}
        data-testid="bio-push-optin-sheet"
      >
        <div className="relative p-5">
          {/* v147.44 — X close button removed. The sheet is now
              modal-until-decided: only the "Not now" and "Enable"
              buttons dismiss it. See the useEffect that re-triggers
              it on every non-subscribed page visit. */}

          {!done && (
            <>
              <div className="flex items-center gap-3 mb-3">
                {picture ? (
                  <img
                    src={picture}
                    alt=""
                    className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                    style={{ border: "2px solid #FCFAF6" }}
                  />
                ) : (
                  <span
                    className="w-11 h-11 rounded-full inline-flex items-center justify-center flex-shrink-0"
                    style={{ background: "color-mix(in srgb, #FF3B30 12%, white)" }}
                  >
                    <Bell className="w-5 h-5" strokeWidth={2.2} style={{ color: "#FF3B30" }} />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#8A8F98" }}>
                    Stay in the loop
                  </p>
                  <h3
                    id="bio-push-optin-title"
                    className="font-display font-black text-[19px] leading-tight truncate"
                    style={{ color: "#161616" }}
                  >
                    Get offers from {label}
                  </h3>
                </div>
              </div>

              <p className="text-sm mt-1 mb-4 leading-relaxed" style={{ color: "#545B66" }}>
                Turn on browser notifications to hear about new coupons,
                products and announcements — even when this page is closed.
              </p>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={notNow}
                  data-testid="bio-push-optin-notnow-btn"
                  className="min-h-[48px] inline-flex items-center justify-center rounded-xl font-semibold text-[15px] transition-colors"
                  style={{
                    background: "var(--ux-surface-2, #F6F3EC)",
                    color: "#161616",
                  }}
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={enable}
                  disabled={busy || status.cap_reached}
                  data-testid="bio-push-optin-enable-btn"
                  className="min-h-[48px] inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] transition-transform hover:translate-y-[-1px] disabled:opacity-60"
                  style={{ background: "#161616", color: "#FFF" }}
                >
                  <Sparkles className="w-4 h-4" strokeWidth={2.4} />
                  {busy ? "Enabling…" : status.cap_reached ? "Full" : "Enable"}
                </button>
              </div>

              <p className="text-[11px] mt-3 text-center" style={{ color: "#8A8F98" }}>
                You can unsubscribe at any time from your browser settings.
              </p>
            </>
          )}

          {done && (
            <div className="text-center py-3" data-testid="bio-push-optin-success">
              <span
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3"
                style={{ background: "color-mix(in srgb, #10B981 15%, white)" }}
              >
                <CheckCircle2 className="w-7 h-7" strokeWidth={2.4} style={{ color: "#10B981" }} />
              </span>
              <h3 className="font-display font-black text-[19px] leading-tight">
                You're all set!
              </h3>
              <p className="text-sm mt-1 mb-4" style={{ color: "#545B66" }}>
                We'll ping you when {label} drops something new.
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="bio-push-optin-done-btn"
                className="min-h-[44px] px-6 rounded-xl font-semibold text-[15px]"
                style={{ background: "#161616", color: "#FFF" }}
              >
                Got it
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
